class NappiUpsell extends HTMLElement {
  static SEEN = "nappi:upsell-seen";
  /* Une seule paire au panier (26/09/2026, auparavant 2). À deux paires, la
     modale vendait une 3e paire à -50 % (85 € les trois) alors que deux paires
     de plus sont offertes (4 paires pour 68 €) : le tiroir le propose à la
     place (snippets/nappi-cart-bundle-nudge.liquid). Au-delà de deux, la
     remise ne s'applique de toute façon plus : voir #offerApplies. */
  static MAX_CART_PAIRS = 1;
  connectedCallback() {
    this.dialog = this.querySelector("dialog");
    if (!this.dialog) return;
    this.accepted = false;
    this.drawer = document.querySelector("cart-drawer-component");
    this.armed = this.dataset.once !== "true" || !this.#seen();
    if (this.armed && this.drawer?.hasAttribute("auto-open")) {
      this.drawer.removeAttribute("auto-open");
      this.autoOpenRemoved = true;
    }
    this.onCartAdd = this.onCartAdd.bind(this);
    document.addEventListener("shopify:cart:lines-update", this.onCartAdd);
    this.querySelector("[data-upsell-accept]")?.addEventListener("click", () => this.accept());
    this.querySelector("[data-upsell-decline]")?.addEventListener("click", () => this.close());
    this.querySelector("[data-upsell-close]")?.addEventListener("click", () => this.close());
    this.dialog.addEventListener("mousedown", (e) => {
      if (e.target === this.dialog) this.close();
    });
    this.dialog.addEventListener("close", () => this.#release());
    this.onKeyDown = (event) => {
      if (event.key !== "Escape" || !this.dialog.open) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.close();
    };
    document.addEventListener("keydown", this.onKeyDown, true);
    this.onPageShow = (event) => {
      if (!event.persisted || !this.accepted) return;
      this.accepted = false;
      const btn = this.querySelector("[data-upsell-accept]");
      const label = this.querySelector("[data-upsell-cta-label]");
      if (btn) btn.disabled = false;
      if (label && this.ctaLabel) label.textContent = this.ctaLabel;
      clearInterval(this.interval);
      this.armed = false;
      if (this.dialog.open) this.dialog.close();
      this.hidden = true;
    };
    window.addEventListener("pageshow", this.onPageShow);
  }
  disconnectedCallback() {
    document.removeEventListener("shopify:cart:lines-update", this.onCartAdd);
    document.removeEventListener("keydown", this.onKeyDown, true);
    window.removeEventListener("pageshow", this.onPageShow);
    clearInterval(this.interval);
  }
  onCartAdd(event) {
    if (event?.action !== "add" || event.context !== "product") return;
    if (this.accepted || this.dialog.open || !this.armed) return;
    const added = typeof event.promise?.then === "function" ? event.promise : Promise.resolve({});
    const variant = Number(String(event.lines?.[0]?.merchandiseId ?? "").match(/(\d+)$/)?.[1]);
    if (Number.isInteger(variant) && variant > 0) this.variantId = variant;
    this.adds = [...this.adds || [], added];
    const seq = this.seq = (this.seq || 0) + 1;
    Promise.allSettled(this.adds).then((results) => {
      if (seq !== this.seq) return null;
      this.adds = [];
      const allFailed = results.every((r) => r.status === "rejected" || r.value?.detail?.didError);
      if (allFailed) return false;
      const last = [...results].reverse().find((r) => r.status === "fulfilled" && r.value?.cart);
      const count = Number(last?.value?.cart?.totalQuantity);
      if (Number.isFinite(count) && count > 0) return count <= NappiUpsell.MAX_CART_PAIRS;
      return this.#offerApplies();
    }).catch(() => false).then((applies) => {
      if (applies === null || seq !== this.seq) return;
      if (applies) {
        requestAnimationFrame(() => {
          if (seq === this.seq && this.armed && !this.accepted) this.open();
        });
      } else {
        this.#release();
      }
    });
  }
  /*
   * Le code de remise est un « achetez 1, la 2e à -50 % » : il lui faut deux
   * paires qu'aucune autre remise n'a prises. Le bundle « Buy 2, Get 2 Free »
   * en prend quatre. Dès que le panier compte trois paires, la paire ajoutée
   * par la modal déclenche le bundle ou ne trouve plus sa paire « achetée », et
   * le code est refusé au paiement — mesuré le 23/09/2026 : 102 € facturés pour
   * 85 € promis. La modal ne s'ouvre donc que si le panier, après l'ajout qui
   * l'a déclenchée, compte au plus MAX_CART_PAIRS paires. En cas de doute
   * (réseau), on ne promet rien.
   */
  async #offerApplies() {
    try {
      const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      if (!res.ok) return false;
      const cart = await res.json();
      return cart.item_count <= NappiUpsell.MAX_CART_PAIRS;
    } catch (e) {
      return false;
    }
  }
  open() {
    if (this.dialog.open) return;
    this.hidden = false;
    this.dialog.showModal();
    this.dialog.focus({ preventScroll: true });
    this.#mark();
    this.#startTimer();
  }
  close() {
    if (this.dialog.open) this.dialog.close();
    else this.#release();
  }
  #release() {
    if (this.dialog.open) return;
    clearInterval(this.interval);
    this.hidden = true;
    const wasArmed = this.armed;
    this.armed = false;
    if (this.autoOpenRemoved) {
      this.drawer?.setAttribute("auto-open", "");
      this.autoOpenRemoved = false;
    }
    if (!this.accepted && wasArmed) {
      requestAnimationFrame(() => this.#openDrawer());
    }
  }
  /* Ouvre le tiroir sans jamais le refermer : le bouton de l'en-tête BASCULE
     le tiroir (#cart-drawer/toggle), un second clic le fermait. On passe donc
     par le tiroir lui-même quand il expose `open()`, le bouton en dernier
     recours. */
  #openDrawer() {
    const themeDrawer = document.getElementById("cart-drawer");
    if (themeDrawer && typeof themeDrawer.open === "function") {
      if (!themeDrawer.isOpen) themeDrawer.open();
      return;
    }
    const opener = document.querySelector('[data-testid="cart-icon"]')?.closest("button, a");
    if (opener instanceof HTMLElement) opener.click();
  }
  #seen() {
    try {
      return sessionStorage.getItem(NappiUpsell.SEEN) === "1";
    } catch (e) {
      return false;
    }
  }
  #mark() {
    try {
      sessionStorage.setItem(NappiUpsell.SEEN, "1");
    } catch (e) {
    }
  }
  #startTimer() {
    const out = this.querySelector("[data-upsell-time]");
    const seconds = parseInt(this.dataset.seconds, 10);
    if (!out || !Number.isFinite(seconds) || seconds <= 0) return;
    let left = seconds;
    const tick = () => {
      const m = String(Math.floor(left / 60)).padStart(2, "0");
      const s = String(left % 60).padStart(2, "0");
      out.textContent = `${m}:${s}`;
      if (left <= 0) {
        clearInterval(this.interval);
        this.close();
      }
      left -= 1;
    };
    tick();
    this.interval = setInterval(tick, 1e3);
  }
  async accept() {
    const btn = this.querySelector("[data-upsell-accept]");
    const label = this.querySelector("[data-upsell-cta-label]");
    if (btn) btn.disabled = true;
    const previous = label?.textContent;
    this.ctaLabel = previous;
    if (label) label.textContent = "…";
    this.accepted = true;
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: this.variantId || Number(this.dataset.variant), quantity: 1 }] })
      });
      if (!res.ok) throw new Error("add failed");
      try {
        sessionStorage.removeItem("cart-count");
      } catch (e) {
      }
    } catch (e) {
      this.accepted = false;
      if (btn) btn.disabled = false;
      if (label && previous) label.textContent = previous;
      return;
    }
    const code = this.dataset.code;
    const root = window.Shopify?.routes?.root || "/";
    window.location.href = code ? `${root}checkout?discount=${encodeURIComponent(code)}` : `${root}checkout`;
  }
}
if (!customElements.get("nappi-upsell")) {
  customElements.define("nappi-upsell", NappiUpsell);
}
